import {
  StickyAssignmentsDocument,
  StickyAttributeKey,
  StickyBucketService,
} from '@growthbook/growthbook-react';

export class RedisStickyBucketService extends StickyBucketService {
  private baseUrl: string;
  private authHeader: string;

  constructor({
    redisUrl,
    redisToken,
  }: {
    redisUrl: string;
    redisToken: string;
  }) {
    super();
    this.baseUrl = redisUrl;
    this.authHeader = `Bearer ${redisToken}`;
  }

  private async request<T>(path: string, options: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}/${path}`, {
      ...options,
      headers: {
        Authorization: this.authHeader,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  getKey(attributeName: string, attributeValue: string): string {
    if (!attributeName || !attributeValue) {
      return '';
    }

    return [attributeName, attributeValue].join('||');
  }

  async getAllAssignments(
    attributes: Record<string, string>
  ): Promise<Record<StickyAttributeKey, StickyAssignmentsDocument>> {
    const docs: Record<StickyAttributeKey, StickyAssignmentsDocument> = {};

    const keys = Object.entries(attributes)
      .map(([attributeName, attributeValue]) => {
        const key = this.getKey(attributeName, attributeValue);
        if (key === '') return null;
        return key;
      })
      .filter((key) => key !== null) as string[];

    if (keys.length === 0) return docs;

    const response = await this.request<{ result: string[] }>(
      `mget/${keys.join('/')}`,
      {
        method: 'POST',
      }
    );

    response.result.forEach((raw) => {
      try {
        const data: {
          attributeName: string;
          attributeValue: string;
          assignments: Record<string, string>;
        } = JSON.parse(raw || '{}');
        if (data.attributeName && data.attributeValue && data.assignments) {
          const key = `${data.attributeName}||${data.attributeValue}`;
          docs[key] = data;
        }
      } catch (e) {
        // ignore parsing errors
      }
    });

    return docs;
  }

  async getAssignments(_attributeName: string, _attributeValue: string) {
    // Not implemented
    return null;
  }

  async saveAssignments(doc: StickyAssignmentsDocument) {
    const key = this.getKey(doc.attributeName, doc.attributeValue);
    await this.request(`set/${key}`, {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  }
}
